import { useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { useInjectReducer, useInjectSaga } from '../../utils/injectors';
import Reducer, { reducer, saga } from './DynamicDialog.reducer';

import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Icon,
  Paper,
  DialogTitle,
  Grid,
} from '@mui/material';
import Draggable from 'react-draggable';
import { useReactToPrint } from 'react-to-print';

import './DynamicDialog.scss';
import * as Constants from '../../utils/constants';
import AnchorMenu from '../../components/AnchorMenu/AnchorMenu';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const key = 'DynamicDialog';

const DynamicDialog = props => {
  const {
    layers,
  } = props;

  useInjectReducer({ key, reducer });
  useInjectSaga({ key, saga });

  return (
    <>
      <section className={key}>
        <ThemeProvider theme={createTheme(Constants.Theme.Default)}>

          {layers.map((layer, index) => (
            <DialogItem
              key={index}
              item={layer}
              {...props}
            />
          ))}

        </ThemeProvider>
      </section>
    </>
  );
};

DynamicDialog.propTypes = {
  layers: PropTypes.arrayOf(PropTypes.object),
};

const mapStateToProps = createStructuredSelector({
  layers: Reducer.makeSelector('layers'),
});

export function mapDispatchToProps(dispatch) {
  return bindActionCreators(Reducer.actionCreators, dispatch);
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  memo,
)(DynamicDialog);

const DialogItem = props => {
  const {
    item,
    close,
  } = props;

  const contentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
  });
  const handleCloseCallback = () => {
    if (item.payload.closeCallback) {
      item.payload.closeCallback();
    }
    close({
      resetLocation: item.payload.resetLocation
    });
  };

  return (
    <Dialog
      open={item.isOpen}
      onClose={handleCloseCallback}
      slotProps={{
        paper: {
          className: `${key} ${item.payload.className ? item.payload.className : ''}`,
        },
      }}
      aria-labelledby="DynamicDialogTitle"
      PaperComponent={PaperComponent}
      fullWidth
      fullScreen={item.payload.fullScreen ? item.payload.fullScreen : false}
      maxWidth={item.payload.maxWidth ? item.payload.maxWidth : 'sm'}
    >
      <Grid
        container
        className="dialog-header"
        alignItems={'center'}
        justifyContent={'flex-end'}
      >

        <Grid className="dialog-title" sx={{ flexGrow: 1 }}>
          <DialogTitle id="DynamicDialogTitle">
            <div className='title dot-overflow'>{item.payload.title}</div>
          </DialogTitle>
        </Grid>

        <Grid className="dialog-title">
          <ActionComponent handlePrint={handlePrint} {...props} />
        </Grid>

      </Grid>

      <DialogContent className='dialog-content' dividers ref={contentRef}>
        {item.payload.content}
      </DialogContent>

      {
        !item.payload.closeSubmitAction &&
        <SubmitActionComponent {...props} />
      }
    </Dialog>
  );
};

export function scrollToStart() {
  const dialogContent = document.querySelector('.DynamicDialog .dialog-content');
  if (dialogContent) {
    dialogContent.scroll(0, 0);
  };
};

const PaperComponent = props => {
  const nodeRef = useRef(null);

  return (
    <Draggable nodeRef={nodeRef} handle="#DynamicDialogTitle" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
};

const ActionComponent = props => {
  const {
    item,
    handlePrint,

    show,
    close,
    setLastLayerStates,
  } = props;

  const { t } = useTranslation();

  const handleCloseCallback = () => {
    if (item.payload.closeCallback) {
      item.payload.closeCallback();
    }
    close({
      resetLocation: item.payload.resetLocation
    });
  };
  const handleDeleteCallback = (customConfirmation, overrideCallback) => {
    show({
      title: customConfirmation?.title ? customConfirmation.title : t('delete'),
      variant: Constants.DynamicDialog.Variant.Confirm.code,
      content: (
        <>
          {
            customConfirmation?.contents ?
              customConfirmation.contents.map((item, index) => {
                return (
                  <p key={index}>{item}</p>
                );
              }) :
              <p>{t('message.deleteConfirmation')}</p>
          }
        </>
      ),
      closeLabel: t('no'),
      acceptLabel: t('yes'),
      acceptCallback: () => {
        if (overrideCallback) {
          overrideCallback();
        }
        else {
          if (item.payload.deleteCallback) {
            item.payload.deleteCallback();
          }
        }
        close();
      },
      maxWidth: "xs"
    });
  };
  const PrintableElement = () => {
    return (
      <>
        {
          item.payload.printable &&
          <IconButton
            color="primary"
            onClick={handlePrint}
          >
            <Icon className="icon">print</Icon>
          </IconButton>
        }
      </>
    );
  };
  const DeleteElement = () => {
    return (
      <>
        {
          item.payload.deleteAnchorItems && item.payload.deleteAnchorItems?.length !== 0 ?
            <AnchorMenu
              anchorButton={(
                <IconButton
                  color="error"
                >
                  <Icon className="icon">delete</Icon>
                </IconButton>
              )}
              menus={item.payload.deleteAnchorItems.map((item) => {
                item.callback = () => {
                  handleDeleteCallback(item.confirmation, item.overrideCallback);
                };
                return item;
              })}
              originVariant={"topRight"}
            /> :
            <IconButton
              color="error"
              onClick={() => { handleDeleteCallback(item.payload.confirmation?.delete) }}
            >
              <Icon className="icon">delete</Icon>
            </IconButton>
        }
      </>
    );
  };
  const EditElement = () => {
    return (
      <>
        {
          item.payload.editAnchorItems && item.payload.editAnchorItems?.length !== 0 ?
            <AnchorMenu
              anchorButton={(
                <IconButton
                  color="primary"
                >
                  <Icon className="icon">edit</Icon>
                </IconButton>
              )}
              menus={item.payload.editAnchorItems.map((x) => {
                x.callback = () => {
                  setLastLayerStates({
                    isEditing: true,
                  });
                  if (x.additionCallback) { x.additionCallback(); }
                };
                return x;
              })}
              originVariant={"topRight"}
            /> :
            <IconButton
              color="primary"
              onClick={() => {
                setLastLayerStates({
                  isEditing: true,
                });
              }}
            >
              <Icon className="icon">edit</Icon>
            </IconButton>
        }
      </>
    );
  };
  const EditOrShowElement = () => {
    return (
      <>
        {
          item.isEditing ?
            <IconButton
              color="primary"
              onClick={() => {
                setLastLayerStates({
                  isEditing: false,
                });
              }}
            >
              <Icon className="icon">visibility</Icon>
            </IconButton> :
            <EditElement />
        }
      </>
    );
  }
  const CloseElement = () => {
    return (
      <IconButton
        color="error"
        aria-label="close"
        onClick={handleCloseCallback}
        className="close-btn"
      >
        <Icon>close</Icon>
      </IconButton>
    );
  }
  const AdditionActionsElement = () => {
    return (
      <>
        {
          item.payload?.additionActions?.map((item, index) => {
            const callback = () => {
              if (item.confirmation) {
                show({
                  title: item.confirmation.title,
                  variant: Constants.DynamicDialog.Variant.Confirm.code,
                  content: (
                    <>
                      {
                        item.confirmation.contents.map((item, index) => {
                          return (
                            <p key={index}>{item}</p>
                          );
                        })
                      }
                    </>
                  ),
                  closeLabel: t('no'),
                  acceptLabel: t('yes'),
                  acceptCallback: () => {
                    item.callback();
                  }
                });
              }
              else {
                item.callback();
              }
            }
            return (
              <IconButton
                key={index}
                color="secondary"
                onClick={callback}
              >
                <Icon className="icon">{item.icon}</Icon>
              </IconButton>
            );
          })
        }
      </>
    );
  }

  switch (item.payload.variant) {
    case Constants.DynamicDialog.Variant.Create.code:
      return (
        <DialogActions>

          <AdditionActionsElement />

          <PrintableElement />

          <CloseElement />

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Update.code:
      return (
        <DialogActions>

          <AdditionActionsElement />

          <PrintableElement />

          {
            item.payload.deleteCallback || item.payload.deleteAnchorItems ?
              <DeleteElement /> : <></>
          }

          <EditOrShowElement />

          <CloseElement />

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Show.code:
      return (
        <DialogActions>

          <AdditionActionsElement />

          <PrintableElement />

          {
            item.payload.deleteCallback || item.payload.deleteAnchorItems ?
              <DeleteElement /> : <></>
          }

          <EditOrShowElement />

          <CloseElement />

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Confirm.code:
      return (
        <DialogActions>

          <AdditionActionsElement />

          <PrintableElement />

          <CloseElement />

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Info.code:
      return (
        <DialogActions>

          <AdditionActionsElement />

          <PrintableElement />

          <CloseElement />

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Panel.code:
      return (
        <DialogActions>

          <AdditionActionsElement />

          <PrintableElement />

          <CloseElement />

        </DialogActions>
      );
    default:
      return (<></>);
  }
};

const SubmitActionComponent = props => {
  const {
    item,

    show,
    close,
  } = props;

  const { t } = useTranslation();

  const handleCloseCallback = () => {
    if (item.payload.closeCallback) {
      item.payload.closeCallback();
    }
    close();
  };
  const handleAcceptCallback = () => {
    if (item.payload.acceptCallback) {
      item.payload.acceptCallback();
    }
    close();
  };
  const handleSubmitCallback = () => {
    // { cancelable: true } required for Firefox
    // https://github.com/facebook/react/issues/12639#issuecomment-382519193
    const target = document.getElementById(item.payload.formId);

    if (target) {
      target.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };
  const AdditionButtonsElement = () => {
    return (
      <>
        {
          item.payload?.additionButtons?.map((item, index) => {
            const itemCallback = async () => {
              if (item.submitCallback) {
                await item.submitCallback();
                handleSubmitCallback();
              }
              else {
                item.callback();
              }
            }
            const callback = () => {
              if (item.confirmation) {
                show({
                  title: item.confirmation.title,
                  variant: Constants.DynamicDialog.Variant.Confirm.code,
                  content: (
                    <>
                      {
                        item.confirmation.contents.map((item, index) => {
                          return (
                            <p key={index}>{item}</p>
                          );
                        })
                      }
                    </>
                  ),
                  closeLabel: t('no'),
                  acceptLabel: t('yes'),
                  acceptCallback: itemCallback,
                });
              }
              else {
                itemCallback();
              }
            }
            return (
              <Button key={index} onClick={callback} {...item.buttonProps}>
                {item.label}
              </Button>
            );
          })
        }
      </>
    );
  };
  const CloseButton = () => {
    return (
      <Button onClick={handleCloseCallback} variant="contained" color="secondary">
        {item.payload.closeLabel ? item.payload.closeLabel : t('close')}
      </Button>
    );
  }

  switch (item.payload.variant) {
    case Constants.DynamicDialog.Variant.Create.code:
      return (
        <DialogActions>

          <CloseButton />

          <AdditionButtonsElement />

          {
            !item.payload.closeSubmitButton &&
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmitCallback}
            >
              {item.payload.submitLabel ? item.payload.submitLabel : t('create')}
            </Button>
          }

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Update.code:
      return (
        <DialogActions>

          <CloseButton />

          <AdditionButtonsElement />

          {
            (item.isEditing && !item.payload.closeSubmitButton) &&
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmitCallback}
            >{item.payload.submitLabel ? item.payload.submitLabel : t('update')}</Button>
          }

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Show.code:
      return (
        <DialogActions>

          <CloseButton />

          <AdditionButtonsElement />

          {
            (item.isEditing && !item.payload.closeSubmitButton) &&
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmitCallback}
            >{item.payload.submitLabel ? item.payload.submitLabel : t('update')}</Button>
          }

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Confirm.code:
      return (
        <DialogActions>

          <Button onClick={handleCloseCallback} color="primary">
            {item.payload.closeLabel ? item.payload.closeLabel : t('no')}
          </Button>

          <Button onClick={handleAcceptCallback} color="primary" autoFocus>
            {item.payload.acceptLabel ? item.payload.acceptLabel : t('yes')}
          </Button>

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.Info.code:
      return (
        <DialogActions>

          <Button onClick={handleAcceptCallback} color="primary" autoFocus>
            {item.payload.acceptLabel ? item.payload.acceptLabel : t('yes')}
          </Button>

        </DialogActions>
      );
    case Constants.DynamicDialog.Variant.UpdatePanel.code:
      return (
        <DialogActions>

          <CloseButton />

          <AdditionButtonsElement />

          {
            (!item.payload.closeSubmitButton) &&
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmitCallback}
            >{item.payload.submitLabel ? item.payload.submitLabel : t('update')}</Button>
          }

        </DialogActions>
      );
    default:
      return (<></>);
  }
};